import { useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  ExternalLink,
  FlaskConical,
  KeyRound,
  Loader2,
  MessageSquareShare,
  PanelRight,
  Radio,
  ShieldCheck,
  Webhook,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useEvotechConnection } from '@/hooks/use-evotech'
import { EvotechConnectPanel, useEvotechPerms } from './evotech-connect'
import { ConnectionsDialog, useMiniApps } from '@/pages/conversations/embedded-apps-dialogs'
import { APP_BY_ID, type AppDef } from './apps-catalog'
import { AppLogo } from './apps-page'

/** Giới hạn của kiểu nhúng form hiện tại — lý do cần app-in-app. */
const EMBED_LIMIT_KEYS = ['noSso', 'noContext', 'noWriteback', 'blocked'] as const

/** Năng lực của kết nối app-in-app chính thức — xem docs/KE-HOACH-APP-IN-APP.md. */
const APP_IN_APP: { icon: LucideIcon; key: 'sso' | 'context' | 'twoWay' | 'webhook' | 'install' }[] = [
  { icon: KeyRound, key: 'sso' },
  { icon: Radio, key: 'context' },
  { icon: MessageSquareShare, key: 'twoWay' },
  { icon: Webhook, key: 'webhook' },
  { icon: ShieldCheck, key: 'install' },
]

export function AppDetailPage() {
  const { t } = useTranslation('chanCatalog')
  const { appId } = useParams()
  const app = appId ? APP_BY_ID[appId] : undefined
  if (!app) return <Navigate to="/integrations/apps" replace />

  return (
    <div className="max-w-5xl space-y-5">
      <Link to="/integrations/apps" className="inline-flex h-14 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {t('appDetail.back')}
      </Link>
      {app.id === 'webview' ? (
        <WebviewApp app={app} />
      ) : app.id === 'evotech' ? (
        <EvotechApp app={app} />
      ) : app.embedId ? (
        <EmbedApp app={app} />
      ) : (
        <BetaApp app={app} />
      )}
    </div>
  )
}

function Header({ app, badge, actions }: { app: AppDef; badge?: React.ReactNode; actions?: React.ReactNode }) {
  const { t } = useTranslation('chanCatalog')
  return (
    <div className="flex flex-wrap items-start gap-4 rounded-xl border bg-card p-5">
      <AppLogo app={app} size="lg" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-medium">{app.name}</h1>
          {badge}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{app.description}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('appsPage.developer', { name: app.developer })}</p>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  )
}

/**
 * App Evotech: hai tab — "Kết nối dữ liệu" (uỷ quyền OAuth + chọn cửa hàng + webhook tự đăng ký: đồng bộ sản phẩm, đơn, hạng/điểm
 * khách — docs/TICH-HOP-EVOTECH.md P1) và "Nhúng form" (iframe ở cột phải Hội thoại, giữ nguyên).
 */
function EvotechApp({ app }: { app: AppDef }) {
  const { t } = useTranslation('chanCatalog')
  // GET /integrations/evotech cần integrations.view — không có thì không gọi, cũng không hiện nhãn trạng thái (sẽ sai).
  const { view: coQuyenXem } = useEvotechPerms()
  const { data: conn } = useEvotechConnection(coQuyenXem)
  return (
    <>
      <Header
        app={app}
        badge={
          !coQuyenXem ? undefined : conn?.connected ? (
            <Badge variant={conn.enabled ? 'success' : 'secondary'}>
              {conn.enabled ? t('appDetail.dataConnected') : t('appDetail.connectionPaused')}
            </Badge>
          ) : (
            <Badge variant="secondary">{t('appDetail.dataNotConnected')}</Badge>
          )
        }
      />
      <Tabs defaultValue="data">
        <TabsList>
          <TabsTrigger value="data">{t('appDetail.tabData')}</TabsTrigger>
          <TabsTrigger value="embed">{t('appsPage.embedForm')}</TabsTrigger>
        </TabsList>
        <TabsContent value="data" className="mt-4">
          <EvotechConnectPanel />
        </TabsContent>
        <TabsContent value="embed" className="mt-4 space-y-5">
          <EmbedApp app={app} inTab />
        </TabsContent>
      </Tabs>
    </>
  )
}

/** Ứng dụng đang chạy bằng nhúng form (tab Nhúng form của App Evotech). */
function EmbedApp({ app, inTab = false }: { app: AppDef; inTab?: boolean }) {
  const { t } = useTranslation('chanCatalog')
  const { apps, save, saving } = useMiniApps()
  const mini = apps.find((m) => m.id === app.embedId)
  const installed = !!mini?.active
  const url = mini?.url ?? app.embedUrl ?? ''

  const setInstalled = (on: boolean) =>
    save(
      mini
        ? apps.map((m) => (m.id === app.embedId ? { ...m, active: on } : m))
        : [...apps, { id: app.embedId!, name: app.name, url, icon: app.icon, active: on }],
    )

  const badge = installed ? <Badge variant="success">{t('appDetail.installedEmbed')}</Badge> : <Badge variant="secondary">{t('appDetail.notInstalled')}</Badge>
  const actions = (
    <>
      <Button variant="outline" asChild>
        <a href={url} target="_blank" rel="noreferrer">
          <ExternalLink /> {t('appDetail.openPage')}
        </a>
      </Button>
      <Button variant={installed ? 'outline' : 'default'} onClick={() => setInstalled(!installed)} disabled={saving}>
        {saving && <Loader2 className="animate-spin" />}
        {installed ? t('appDetail.uninstall') : t('appDetail.install')}
      </Button>
    </>
  )

  return (
    <>
      {inTab ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-4">
          <PanelRight className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{t('appDetail.tabInRail', { name: app.name })}</span>
          {badge}
          <div className="ml-auto flex items-center gap-2">{actions}</div>
        </div>
      ) : (
        <Header app={app} badge={badge} actions={actions} />
      )}

      <h2 className="text-base font-medium">{t('appDetail.howToConnect')}</h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex flex-col rounded-xl border bg-card p-5">
          <div className="flex items-center gap-2">
            <PanelRight className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-medium">{t('appDetail.webviewTitle')}</h3>
            <Badge variant={installed ? 'success' : 'secondary'} className="ml-auto">
              {installed ? t('appDetail.inUse') : t('appDetail.ready')}
            </Badge>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            <Trans t={t} i18nKey="appDetail.openInTab" values={{ url }} components={{ url: <span className="font-mono text-foreground" /> }} />
          </p>
          <p className="mb-2 mt-4 text-xs font-medium text-muted-foreground">{t('appDetail.limits')}</p>
          <ul className="space-y-1.5 text-sm">
            {EMBED_LIMIT_KEYS.map((l) => (
              <li key={l} className="flex gap-2">
                <Circle className="mt-1.5 h-2 w-2 shrink-0 fill-muted-foreground text-muted-foreground" /> {t(`appDetail.limit.${l}`)}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col rounded-xl border-2 border-primary/40 bg-gradient-to-b from-primary/[0.04] to-transparent p-5">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-primary" />
            <h3 className="font-medium">{t('appDetail.appInAppTitle')}</h3>
            <Badge className="ml-auto">{t('appDetail.betaInDev')}</Badge>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('appDetail.runsAsPart', { name: app.name })}
          </p>
          <ul className="mt-4 space-y-3">
            {APP_IN_APP.map((f) => (
              <li key={f.key} className="flex gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <f.icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-medium">{t(`appDetail.appInApp.${f.key}.title`)}</p>
                  <p className="text-xs text-muted-foreground">{t(`appDetail.appInApp.${f.key}.desc`)}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-auto flex items-center justify-end gap-3 pt-5">
            <span className="text-xs text-muted-foreground">{t('appDetail.openWhenReady')}</span>
            <Button disabled>
              <CheckCircle2 /> {t('appDetail.switchToAppInApp')}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

/** "Nhúng trang web tuỳ chỉnh" — quản lý danh sách trang nhúng hiện có. */
function WebviewApp({ app }: { app: AppDef }) {
  const { t } = useTranslation('chanCatalog')
  const [open, setOpen] = useState(false)
  const { apps } = useMiniApps()
  return (
    <>
      <Header
        app={app}
        badge={<Badge variant="secondary">{t('appDetail.embedPages', { count: apps.length })}</Badge>}
        actions={<Button onClick={() => setOpen(true)}>{t('appDetail.manageEmbedPages')}</Button>}
      />
      <p className="text-sm text-muted-foreground">
        {t('appDetail.embedPagesHelp')}
      </p>
      <ConnectionsDialog open={open} onOpenChange={setOpen} />
    </>
  )
}

/** Ứng dụng chưa kết nối được — trang giới thiệu. */
function BetaApp({ app }: { app: AppDef }) {
  const { t } = useTranslation('chanCatalog')
  return (
    <>
      <Header
        app={app}
        badge={<Badge>Beta</Badge>}
        actions={<Button disabled>{t('common:actions.connect')}</Button>}
      />
      <div className="flex gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
        <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-muted-foreground">
          {app.betaNote ?? t('appDetail.inDevelopment')} {t('appDetail.willShareFrame')}
        </p>
      </div>
    </>
  )
}
